/**
 * PartRateDbView.js
 *
 * Read/(restricted)write view of the lt_part_rate_db lookup table ("Part Rate DB").
 * This is the MCAA / OTT labour-hour & rate reference data (~370k rows), so the
 * grid is server-paginated, searchable and filterable.
 *
 * Backend: /api/lt-part-rate-db
 *   - GET /                       paginated list (+ canEdit flag)
 *   - GET /distinct/:column       distinct values for filter dropdowns
 *   - GET /export                 streamed xlsx (mode=dashboard|all|template)
 *   - POST /import                append rows (edit only)
 *   - POST / PUT /:id / DELETE /:id   restricted to superadmin + edit allowlist
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from 'recharts';
import { authenticatedApiCall } from '../../../utils/apiClient';
import GridExportModal from '../../common/GridExportModal';
import { ListenButton } from '../../common/AudioGuide';
import HelpWithVideoTour from '../../Help/HelpWithVideoTour';
import AuditLogModal from '../../common/AuditLogModal';
import PartRateDbAnalytics from './PartRateDbAnalytics';
import UploadModal from '../../common/UploadModal';
import { UPLOAD_CONFIGS } from '../../../config/uploadConfigs';
import { FilterIcon, FilterTemplateDropdown } from '../../common/FilteringTemplate';
import { usePresence, ActiveViewers } from '../../common/ViewingTemplate';
import useServerFilterTemplate from '../../../hooks/useServerFilterTemplate';
import useColumnOrder from '../../../hooks/useColumnOrder';
import ColumnConfigPanel from '../../common/ColumnConfigPanel';
import { applyColumnOrder } from '../../../utils/columnLayout';

// Accepted Joint Type values (shared concept with the MTO grid). Editable via dropdown.
const JOIN_TYPE_OPTIONS = ['Welded', 'Threaded', 'Threaded+Welded', 'Victaulic', 'PVC', 'HDPE'];

const FILTER_COLUMNS = [
  { key: 'material', label: 'Material' },
  { key: 'description', label: 'Description' },
  { key: 'sch', label: 'Sch' },
  { key: 'rating', label: 'Rating' },
  { key: 'end_type', label: 'End Type' },
  { key: 'seam_type', label: 'Seam' },
  { key: 'join_type', label: 'Joint Type' },
  { key: 'mcaa_location', label: 'Location' },
];

// Columns with a backend /distinct endpoint -> get the "Select Values" checkbox
// tab in the header filter dropdown. High-cardinality columns (part #, matchkey,
// unique key) and numeric columns get the Advanced tab only.
const DISTINCT_COLUMNS = ['material', 'description', 'sch', 'rating', 'end_type', 'seam_type', 'join_type', 'mcaa_location', 'size'];
const NO_FILTER_COLUMNS = new Set(['datasheet']);

// Display columns (in order). `editable` marks columns editable by allowed users.
const COLUMNS = [
  { key: 'part_number', label: 'Part #', editable: false, width: 110 },
  { key: 'material', label: 'Material', editable: true, width: 110 },
  { key: 'description', label: 'Description', editable: true, width: 150 },
  { key: 'size', label: 'Size', editable: true, width: 80 },
  { key: 'sch', label: 'Sch', editable: true, width: 90 },
  { key: 'rating', label: 'Rating', editable: true, width: 90 },
  { key: 'end_type', label: 'End Type', editable: true, width: 90 },
  { key: 'seam_type', label: 'Seam', editable: true, width: 70 },
  { key: 'join_type', label: 'Joint Type', editable: true, width: 120, options: JOIN_TYPE_OPTIONS },
  { key: 'ott_hour', label: 'OTT Hour', editable: true, width: 90, numeric: true },
  { key: 'mcaa_rate', label: 'MCAA Rate', editable: true, width: 90, numeric: true },
  { key: 'cost_cad', label: 'Cost CAD', editable: true, width: 90, numeric: true },
  { key: 'datasheet', label: 'Datasheet', editable: false, width: 160 },
  { key: 'joints', label: 'Joints', editable: true, width: 70, numeric: true },
  { key: 'dia_in', label: 'Dia In', editable: true, width: 70, numeric: true },
  { key: 'mcaa_location', label: 'Location', editable: true, width: 130 },
  { key: 'matchkey', label: 'Matchkey', editable: true, width: 160 },
  { key: 'unique_key', label: 'Unique Key', editable: true, width: 220 },
];

const BTN = {
  padding: '6px 12px', backgroundColor: '#f3f4f6', color: '#374151',
  border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer',
  fontSize: '0.8rem', fontWeight: 500, whiteSpace: 'nowrap',
};

// Infinite scroll: rows load in batches of this size as the user scrolls
// (replaces the old page-by-page footer over ~370k rows).
const SCROLL_PAGE_SIZE = 300;

const PartRateDbView = () => {
  const { instance, accounts } = useMsal();
  const { viewers, isConnected, connectionState } = usePresence('partRateDb', 'global', true);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [pagesLoaded, setPagesLoaded] = useState(0);
  const [canEdit, setCanEdit] = useState(false);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({});
  const [distinct, setDistinct] = useState({});
  const [distinctBlanks, setDistinctBlanks] = useState({});
  const [sortBy, setSortBy] = useState('part_number');
  const [sortDir, setSortDir] = useState('ASC');

  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [toast, setToast] = useState(null);

  // Batch actions: explicit row selection, or "all matching filters" mode
  // (server-side scope — lets Check All cover rows that were never loaded).
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [selectAllMatching, setSelectAllMatching] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const [showColumnConfig, setShowColumnConfig] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(() => new Set(COLUMNS.map((c) => c.key)));
  const { columnOrder, moveColumn } = useColumnOrder('part-rate-db-column-order', COLUMNS);
  const [showExport, setShowExport] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [showCostImport, setShowCostImport] = useState(false);
  const [costConflicts, setCostConflicts] = useState(null); // [{ part_number, costs, rows_by_cost }]
  const [showAudit, setShowAudit] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [autoFitWidths, setAutoFitWidths] = useState(null); // {colKey: px} when autofit is active
  const columnConfigRef = useRef(null);

  // Price-history modal (Plan 2)
  const [historyPart, setHistoryPart] = useState(null); // part_number string
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const openPriceHistory = async (item) => {
    setHistoryPart(item.part_number);
    setHistoryLoading(true);
    setHistoryData([]);
    try {
      const r = await authenticatedApiCall(instance, accounts, 'GET', `/lt-part-rate-db/${item.id}/price-history`);
      if (r.data?.success) setHistoryData(r.data.data || []);
    } catch (err) {
      showToast('Failed to load price history', 'error');
    } finally {
      setHistoryLoading(false);
    }
  };

  // Datasheet (spec) versions modal — mirrors the price-history trend.
  const [dsItem, setDsItem] = useState(null); // { id, part_number }
  const [dsData, setDsData] = useState([]);
  const [dsLoading, setDsLoading] = useState(false);
  const [dsUploading, setDsUploading] = useState(false);
  const dsFileRef = useRef(null);

  const openDatasheetHistory = async (item) => {
    setDsItem({ id: item.id, part_number: item.part_number });
    setDsLoading(true);
    setDsData([]);
    try {
      const r = await authenticatedApiCall(instance, accounts, 'GET', `/lt-part-rate-db/${item.id}/datasheet-history`);
      if (r.data?.success) setDsData(r.data.data || []);
    } catch (err) {
      showToast('Failed to load datasheet history', 'error');
    } finally {
      setDsLoading(false);
    }
  };

  const viewDatasheet = async (datasheetId) => {
    if (!datasheetId) return;
    try {
      const resp = await authenticatedApiCall(instance, accounts, 'GET', `/lt-part-rate-db/datasheet/${datasheetId}/file`, null, { responseType: 'blob' });
      const url = URL.createObjectURL(resp.data);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) { showToast('Failed to open datasheet', 'error'); }
  };

  const onDatasheetPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !dsItem) return;
    if (file.type !== 'application/pdf') { showToast('Only PDF files are allowed', 'error'); return; }
    setDsUploading(true);
    try {
      const fd = new FormData();
      fd.append('pdf', file);
      const resp = await authenticatedApiCall(instance, accounts, 'POST', `/lt-part-rate-db/${dsItem.id}/datasheet`, fd);
      if (resp.data?.success) { showToast('Datasheet uploaded'); await openDatasheetHistory({ id: dsItem.id, part_number: dsItem.part_number }); fetchItems(); }
      else showToast(resp.data?.message || 'Upload failed', 'error');
    } catch (err) { showToast(err?.response?.data?.message || 'Upload failed', 'error'); }
    finally { setDsUploading(false); }
  };

  const visibleCols = applyColumnOrder(COLUMNS, columnOrder).filter((c) => visibleColumns.has(c.key));

  // Column-header filters (Filtering Template UX, server-side). The distinct
  // values feed the "Select Values" tab; '(Blank)' is appended when the column
  // has NULL/empty rows.
  const filterUniqueValues = React.useMemo(() => {
    const out = {};
    DISTINCT_COLUMNS.forEach((key) => {
      const vals = (distinct[key] || []).map(String);
      if (distinctBlanks[key]) vals.push('(Blank)');
      out[key] = vals;
    });
    return out;
  }, [distinct, distinctBlanks]);

  const headerFilter = useServerFilterTemplate({
    columns: COLUMNS,
    uniqueValues: filterUniqueValues,
    numericColumnKeys: COLUMNS.filter((c) => c.numeric).map((c) => c.key),
  });
  const { afParam } = headerFilter;

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Debounce the search box.
  const debounceRef = useRef(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(searchInput.trim());
    }, 400);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [searchInput]);

  // Close column config on outside click.
  useEffect(() => {
    if (!showColumnConfig) return;
    const handler = (e) => {
      if (columnConfigRef.current && !columnConfigRef.current.contains(e.target)) setShowColumnConfig(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showColumnConfig]);

  const buildQuery = useCallback((pageNum, pageSize = SCROLL_PAGE_SIZE) => {
    const params = new URLSearchParams();
    params.set('page', String(pageNum));
    params.set('pageSize', String(pageSize));
    params.set('sortBy', sortBy);
    params.set('sortDir', sortDir);
    if (search) params.set('search', search);
    Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
    if (afParam) params.set('af', afParam);
    return params.toString();
  }, [sortBy, sortDir, search, filters, afParam]);

  // Infinite scroll state machine. `fetchSeq` invalidates in-flight responses
  // when the query (search/filter/sort) changes under them.
  const fetchSeqRef = useRef(0);
  const loadingMoreRef = useRef(false);
  const hasMore = pagesLoaded * SCROLL_PAGE_SIZE < total;

  const fetchFirstPage = useCallback(async () => {
    const seq = ++fetchSeqRef.current;
    try {
      setLoading(true);
      const result = await authenticatedApiCall(instance, accounts, 'GET', `/lt-part-rate-db?${buildQuery(1)}`);
      if (seq !== fetchSeqRef.current) return;
      if (result.data?.success) {
        setItems(result.data.data || []);
        setTotal(result.data.total || 0);
        setPagesLoaded(1);
        setCanEdit(!!result.data.canEdit);
      }
    } catch (err) {
      if (seq !== fetchSeqRef.current) return;
      console.error('Error fetching part rate db:', err);
      showToast('Failed to load data', 'error');
    } finally {
      if (seq === fetchSeqRef.current) setLoading(false);
    }
  }, [instance, accounts, buildQuery]);

  // Query changed (search/filters/sort/header filters) -> reload from the top.
  useEffect(() => { fetchFirstPage(); }, [fetchFirstPage]);

  const loadMore = useCallback(async () => {
    if (loadingMoreRef.current || loading) return;
    if (pagesLoaded * SCROLL_PAGE_SIZE >= total) return;
    loadingMoreRef.current = true;
    const seq = fetchSeqRef.current;
    const nextPage = pagesLoaded + 1;
    try {
      setLoadingMore(true);
      const result = await authenticatedApiCall(instance, accounts, 'GET', `/lt-part-rate-db?${buildQuery(nextPage)}`);
      if (seq !== fetchSeqRef.current) return; // query changed while loading
      if (result.data?.success) {
        setItems((prev) => [...prev, ...(result.data.data || [])]);
        setTotal(result.data.total || 0);
        setPagesLoaded(nextPage);
      }
    } catch (err) {
      if (seq === fetchSeqRef.current) showToast('Failed to load more rows', 'error');
    } finally {
      loadingMoreRef.current = false;
      setLoadingMore(false);
    }
  }, [instance, accounts, buildQuery, pagesLoaded, total, loading]);

  const handleGridScroll = useCallback((e) => {
    const el = e.currentTarget;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 800) loadMore();
  }, [loadMore]);

  // After a mutation (edit/delete/flag/import), re-fetch every currently loaded
  // page in parallel so the grid refreshes without losing scroll position.
  // Deep-scrolled sessions (>10 pages) just reload from the top instead.
  const refreshLoaded = useCallback(async () => {
    const pages = Math.max(pagesLoaded, 1);
    if (pages > 10) { fetchFirstPage(); return; }
    const seq = ++fetchSeqRef.current;
    try {
      const results = await Promise.all(
        Array.from({ length: pages }, (_, i) =>
          authenticatedApiCall(instance, accounts, 'GET', `/lt-part-rate-db?${buildQuery(i + 1)}`)
        )
      );
      if (seq !== fetchSeqRef.current) return;
      const rows = results.flatMap((r) => (r.data?.success ? r.data.data || [] : []));
      const first = results[0];
      if (first.data?.success) {
        setItems(rows);
        setTotal(first.data.total || 0);
        setPagesLoaded(pages);
        setCanEdit(!!first.data.canEdit);
      }
    } catch (err) {
      if (seq === fetchSeqRef.current) showToast('Failed to refresh data', 'error');
    }
  }, [instance, accounts, buildQuery, pagesLoaded, fetchFirstPage]);

  // Existing mutation handlers call fetchItems(); keep that name pointing at
  // the position-preserving refresh.
  const fetchItems = refreshLoaded;

  // Load distinct values for filter dropdowns once (toolbar + header filters).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const out = {};
      const blanks = {};
      for (const key of DISTINCT_COLUMNS) {
        try {
          const r = await authenticatedApiCall(instance, accounts, 'GET', `/lt-part-rate-db/distinct/${key}`);
          if (r.data?.success) { out[key] = r.data.data || []; blanks[key] = !!r.data.hasBlank; }
        } catch (_) { /* ignore */ }
      }
      if (!cancelled) { setDistinct(out); setDistinctBlanks(blanks); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, accounts]);

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir((d) => (d === 'ASC' ? 'DESC' : 'ASC'));
    else { setSortBy(key); setSortDir('ASC'); }
  };

  const setFilter = (key, value) => {
    setFilters((prev) => {
      const next = { ...prev };
      if (value) next[key] = value; else delete next[key];
      return next;
    });
  };

  const clearAll = () => { setSearchInput(''); setSearch(''); setFilters({}); headerFilter.clearAllFilters(); };

  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) { if (next.size > 1) next.delete(key); } else next.add(key);
      return next;
    });
  };
  const showAllColumns = () => setVisibleColumns(new Set(COLUMNS.map((c) => c.key)));
  const minColumns = () => setVisibleColumns(new Set(['part_number', 'material', 'description', 'ott_hour', 'mcaa_rate']));

  const startEdit = (item) => {
    setEditingId(item.id);
    const form = {};
    COLUMNS.forEach((c) => { if (c.editable) form[c.key] = item[c.key] ?? ''; });
    setEditForm(form);
  };

  const handleSave = async () => {
    try {
      await authenticatedApiCall(instance, accounts, 'PUT', `/lt-part-rate-db/${editingId}`, editForm);
      setEditingId(null);
      fetchItems();
      showToast('Saved');
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to save', 'error');
    }
  };

  const handleAdd = async () => {
    try {
      const r = await authenticatedApiCall(instance, accounts, 'POST', '/lt-part-rate-db', { description: 'New Part' });
      showToast(`Added part ${r.data?.data?.part_number || ''}`);
      fetchItems();
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to add', 'error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this part?')) return;
    try {
      await authenticatedApiCall(instance, accounts, 'DELETE', `/lt-part-rate-db/${id}`);
      fetchItems();
      showToast('Deleted');
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to delete', 'error');
    }
  };

  // ---- Batch actions (checkbox selection) ----
  const toggleSelect = (id) => {
    setSelectAllMatching(false);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const allLoadedSelected = selectAllMatching || (items.length > 0 && items.every((it) => selectedIds.has(it.id)));
  const someLoadedSelected = selectAllMatching || items.some((it) => selectedIds.has(it.id));

  const toggleSelectLoaded = () => {
    if (selectAllMatching) { setSelectAllMatching(false); setSelectedIds(new Set()); return; }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allLoadedSelected) items.forEach((it) => next.delete(it.id));
      else items.forEach((it) => next.add(it.id));
      return next;
    });
  };

  const clearSelection = () => { setSelectedIds(new Set()); setSelectAllMatching(false); };

  // Selection is scoped to the current search/filters; drop it when they change.
  useEffect(() => { clearSelection(); }, [search, filters, afParam, sortBy, sortDir]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedCount = selectAllMatching ? total : selectedIds.size;

  const runBulk = async (action) => {
    if (bulkBusy || selectedCount === 0) return;
    const count = selectedCount;
    let reason;
    if (action === 'delete') {
      if (selectAllMatching) {
        // Server-side delete of every matching row (possibly the whole table)
        // — require a typed confirmation, not just an OK click.
        const typed = window.prompt(
          `This will permanently delete ALL ${count.toLocaleString()} rows matching the current filters — including rows not loaded on screen. This cannot be undone.\n\nType DELETE to confirm:`
        );
        if (typed === null) return;
        if (typed.trim().toUpperCase() !== 'DELETE') { showToast('Delete cancelled — confirmation text did not match', 'error'); return; }
      } else if (!window.confirm(`Delete ${count.toLocaleString()} selected part${count === 1 ? '' : 's'}? This cannot be undone.`)) {
        return;
      }
    } else if (action === 'flag') {
      reason = window.prompt(`Reason for flagging ${count.toLocaleString()} selected rate${count === 1 ? '' : 's'}:`, '');
      if (reason === null) return; // cancelled
    }
    setBulkBusy(true);
    try {
      const body = selectAllMatching
        ? { action, all: true, ...(search ? { search } : {}), ...filters, ...(afParam ? { af: afParam } : {}), ...(action === 'flag' ? { reason } : {}) }
        : { action, ids: [...selectedIds], ...(action === 'flag' ? { reason } : {}) };
      const r = await authenticatedApiCall(instance, accounts, 'POST', '/lt-part-rate-db/bulk', body);
      showToast(r.data?.message || 'Done');
      clearSelection();
      fetchItems();
    } catch (err) {
      showToast(err?.response?.data?.message || 'Bulk action failed', 'error');
    } finally {
      setBulkBusy(false);
    }
  };

  const handleFlag = async (item) => {
    const reason = window.prompt('Reason for flagging this rate (the lead disagrees with it):', item.flag_reason || '');
    if (reason === null) return; // cancelled
    try {
      await authenticatedApiCall(instance, accounts, 'POST', `/lt-part-rate-db/${item.id}/flag`, { reason });
      fetchItems();
      showToast('Rate flagged');
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to flag', 'error');
    }
  };

  const handleUnflag = async (item) => {
    try {
      await authenticatedApiCall(instance, accounts, 'POST', `/lt-part-rate-db/${item.id}/unflag`, {});
      fetchItems();
      showToast('Flag cleared');
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to unflag', 'error');
    }
  };

  const handleRevertCost = async (item) => {
    if (!window.confirm('Revert this part\'s cost back to its previous price?')) return;
    try {
      const r = await authenticatedApiCall(instance, accounts, 'POST', `/lt-part-rate-db/${item.id}/revert-cost`, {});
      fetchItems();
      showToast(r?.data?.message || 'Cost reverted');
    } catch (err) {
      showToast(err?.response?.data?.message || 'Failed to revert', 'error');
    }
  };

  const handleExport = async (mode) => {
    if (exporting) return;
    setExporting(true);
    setShowExport(false);
    try {
      const params = new URLSearchParams();
      params.set('mode', mode);
      if (mode === 'dashboard') params.set('columns', visibleCols.map((c) => c.key).join(','));
      if (mode !== 'template') {
        if (search) params.set('search', search);
        Object.entries(filters).forEach(([k, v]) => { if (v) params.set(k, v); });
        if (afParam) params.set('af', afParam);
      }
      const tokenResponse = await instance.acquireTokenSilent({ scopes: ['openid', 'profile', 'email'], account: accounts[0] });
      const apiUrl = process.env.REACT_APP_API_URL || '/api';
      const resp = await fetch(`${apiUrl}/lt-part-rate-db/export?${params.toString()}`, { headers: { Authorization: `Bearer ${tokenResponse.idToken}` } });
      if (!resp.ok) throw new Error(`Export failed: ${resp.status}`);
      const buf = await resp.arrayBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `part_rate_db_${mode}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      showToast(mode === 'template' ? 'Template downloaded' : 'Export downloaded');
    } catch (e) {
      showToast(e.message || 'Export failed', 'error');
    } finally {
      setExporting(false);
    }
  };

  const fmtVal = (col, v) => {
    if (v === null || v === undefined || v === '') return col.numeric ? '—' : '—';
    if (col.numeric) {
      const n = Number(v);
      return Number.isFinite(n) ? n.toFixed(2) : v;
    }
    return v;
  };

  // Autofit: size each visible column to the widest value currently loaded
  // (header + rows on this page). Toggles off to restore the default widths.
  const handleAutoFit = () => {
    if (autoFitWidths) { setAutoFitWidths(null); return; }
    const widths = {};
    visibleCols.forEach((c) => {
      // Header needs room for the label + sort arrow + funnel filter icon; wide
      // all-caps labels (e.g. "MCAA RATE") otherwise clip the icon under the
      // next header cell in fixed table layout.
      let maxLen = String(c.label || '').length + 5;
      items.forEach((item) => {
        const len = String(fmtVal(c, item[c.key]) ?? '').length;
        if (len > maxLen) maxLen = len;
      });
      widths[c.key] = Math.min(360, Math.max(60, Math.round(maxLen * 7.5) + 20));
    });
    setAutoFitWidths(widths);
  };
  const colWidth = (c) => (autoFitWidths && autoFitWidths[c.key]) || c.width;

  const thStyle = { padding: '8px 10px', fontSize: '0.68rem', color: '#6b7280', textTransform: 'uppercase', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb', textAlign: 'left', whiteSpace: 'nowrap', cursor: 'pointer', position: 'sticky', top: 0 };
  const tdStyle = { padding: '5px 10px', fontSize: '0.78rem', borderBottom: '1px solid #f3f4f6', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
  const ACTIONS_WIDTH = 230;
  const inputStyle = { padding: '3px 6px', borderRadius: '4px', border: '1px solid #d1d5db', fontSize: '0.75rem', width: '100%', boxSizing: 'border-box' };
  const selStyle = { padding: '6px 8px', borderRadius: '6px', border: '1px solid #dee2e6', fontSize: '0.8rem', maxWidth: '170px' };

  return (
    <div style={{ padding: '24px' }}>
      {toast && (
        <div style={{ position: 'fixed', top: '20px', right: '20px', zIndex: 9999, padding: '12px 20px', borderRadius: '8px', color: 'white', fontWeight: '500', backgroundColor: toast.type === 'error' ? '#dc3545' : '#198754', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' }}>
          {toast.message}
        </div>
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', gap: '12px', flexWrap: 'wrap' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.3rem', color: '#1f2937' }}>Part Rate DB</h2>
          <p style={{ margin: '4px 0 0', color: '#6b7280', fontSize: '0.85rem' }}>
            MCAA / OTT labour-hour &amp; rate reference (lt_part_rate_db) — {total.toLocaleString()} parts
            {!canEdit && <span style={{ marginLeft: 8, color: '#9ca3af' }}>· view only</span>}
          </p>
        </div>

        {/* Toolbar */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', flexWrap: 'wrap', position: 'relative' }}>
          <div style={{ position: 'relative' }} ref={columnConfigRef}>
            <button onClick={() => setShowColumnConfig(!showColumnConfig)} style={{ ...BTN, backgroundColor: showColumnConfig ? '#6366f1' : '#f3f4f6', color: showColumnConfig ? 'white' : '#374151', borderColor: showColumnConfig ? '#6366f1' : '#e5e7eb' }}>⚙️ Columns</button>
            {showColumnConfig && (
              <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, backgroundColor: 'white', borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', padding: 12, zIndex: 1000, width: 320, maxHeight: 440, overflowY: 'auto' }}>
                <ColumnConfigPanel
                  title="Columns"
                  columns={applyColumnOrder(COLUMNS, columnOrder)}
                  isVisible={(key) => visibleColumns.has(key)}
                  onToggle={toggleColumn}
                  onMove={moveColumn}
                  onShowAll={showAllColumns}
                  onHideAll={minColumns}
                />
              </div>
            )}
          </div>
          <button
            onClick={handleAutoFit}
            title="Resize columns to fit their contents"
            style={{ ...BTN, backgroundColor: autoFitWidths ? '#6366f1' : '#f3f4f6', color: autoFitWidths ? 'white' : '#374151', borderColor: autoFitWidths ? '#6366f1' : '#e5e7eb' }}
          >
            ↔ Autofit
          </button>
          <ActiveViewers
            viewers={viewers}
            isConnected={isConnected}
            connectionState={connectionState}
            maxVisible={4}
            showConnectionStatus={true}
          />
          <button onClick={() => setShowHelp(true)} style={BTN}>❓ Help</button>
          <button onClick={() => setShowAnalytics(true)} style={BTN}>📊 Analytics</button>
          <button onClick={() => setShowExport(true)} style={BTN}>📤 Export</button>
          <button onClick={() => setShowAudit(true)} style={BTN}>📜 Audit Log</button>
          {canEdit && <button onClick={() => setShowImport(true)} style={BTN}>📥 Import</button>}
          {canEdit && <button onClick={() => setShowCostImport(true)} style={BTN}>💲 Import Cost</button>}
          {canEdit && <button onClick={handleAdd} style={BTN}>➕ Add Part</button>}
        </div>
      </div>

      {/* Search + filters */}
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap', marginBottom: '12px' }}>
        <input
          type="text"
          placeholder="Search part #, material, description, size, matchkey..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          style={{ padding: '8px 14px', borderRadius: '8px', border: '1px solid #dee2e6', fontSize: '0.9rem', width: '340px', outline: 'none' }}
        />
        {FILTER_COLUMNS.map(({ key, label }) => (
          <select key={key} value={filters[key] || ''} onChange={(e) => setFilter(key, e.target.value)} style={selStyle}>
            <option value="">{label}: All</option>
            {(distinct[key] || []).map((v) => (<option key={v} value={v}>{v}</option>))}
          </select>
        ))}
        {(search || Object.keys(filters).length > 0 || afParam) && (
          <button onClick={clearAll} style={{ padding: '7px 12px', borderRadius: '6px', border: '1px solid #dee2e6', backgroundColor: '#fff', cursor: 'pointer', fontSize: '0.8rem', color: '#6b7280' }}>
            Clear
          </button>
        )}
      </div>

      {/* Active column-header filters (chips) */}
      {afParam && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
          <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#374151' }}>Column filters:</span>
          {headerFilter.getActiveFilters().map((f) => (
            <span key={`${f.key}-${f.type}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '3px 8px', backgroundColor: '#d1fae5', borderRadius: '4px', fontSize: '0.75rem', color: '#065f46' }}>
              {headerFilter.columnLabels[f.key] || f.key}: {f.type === 'values' ? `${f.count} value${f.count === 1 ? '' : 's'}` : `${f.mode} "${f.value || ''}"`}
              <button
                type="button"
                onClick={() => headerFilter.clearColumnFilter(f.key)}
                title="Remove filter"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#065f46', padding: 0, fontSize: '0.85rem', lineHeight: 1 }}
              >
                ×
              </button>
            </span>
          ))}
          <button
            type="button"
            onClick={() => headerFilter.clearAllFilters()}
            style={{ padding: '3px 10px', fontSize: '0.75rem', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
          >
            Clear all
          </button>
        </div>
      )}

      {/* Batch action bar */}
      {canEdit && selectedCount > 0 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '12px', padding: '8px 14px', backgroundColor: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: '8px' }}>
          <span style={{ fontSize: '0.82rem', fontWeight: 600, color: '#3730a3' }}>
            {selectAllMatching
              ? `All ${total.toLocaleString()} matching rows selected`
              : `${selectedIds.size.toLocaleString()} selected`}
          </span>
          {!selectAllMatching && total > items.length && allLoadedSelected && (
            <button onClick={() => setSelectAllMatching(true)} disabled={bulkBusy}
              style={{ padding: '5px 12px', backgroundColor: '#fff', border: '1px solid #c7d2fe', borderRadius: '6px', cursor: bulkBusy ? 'not-allowed' : 'pointer', fontSize: '0.78rem', color: '#4338ca', fontWeight: 600 }}>
              Select all {total.toLocaleString()} matching
            </button>
          )}
          <button onClick={() => runBulk('flag')} disabled={bulkBusy}
            style={{ padding: '5px 12px', backgroundColor: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '6px', cursor: bulkBusy ? 'not-allowed' : 'pointer', fontSize: '0.78rem', color: '#c2410c', fontWeight: 500 }}>
            🚩 Flag
          </button>
          <button onClick={() => runBulk('unflag')} disabled={bulkBusy}
            style={{ padding: '5px 12px', backgroundColor: '#fff', border: '1px solid #fed7aa', borderRadius: '6px', cursor: bulkBusy ? 'not-allowed' : 'pointer', fontSize: '0.78rem', color: '#c2410c', fontWeight: 500 }}>
            Unflag
          </button>
          <button onClick={() => runBulk('delete')} disabled={bulkBusy}
            style={{ padding: '5px 12px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', cursor: bulkBusy ? 'not-allowed' : 'pointer', fontSize: '0.78rem', color: '#dc3545', fontWeight: 500 }}>
            🗑 Delete
          </button>
          <button onClick={clearSelection} disabled={bulkBusy}
            style={{ padding: '5px 12px', backgroundColor: 'transparent', border: 'none', cursor: bulkBusy ? 'not-allowed' : 'pointer', fontSize: '0.78rem', color: '#6366f1', textDecoration: 'underline' }}>
            Clear selection
          </button>
          {bulkBusy && <span style={{ fontSize: '0.78rem', color: '#6b7280' }}>Working…</span>}
        </div>
      )}

      <div onScroll={handleGridScroll} style={{ backgroundColor: 'white', borderRadius: '10px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto', maxHeight: '62vh', overflowY: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1000px', tableLayout: autoFitWidths ? 'fixed' : 'auto' }}>
          <thead>
            <tr>
              {canEdit && (
                <th style={{ ...thStyle, cursor: 'default', width: 34, minWidth: 34 }}>
                  <input
                    type="checkbox"
                    title={allLoadedSelected ? 'Unselect all' : 'Select all loaded'}
                    checked={allLoadedSelected}
                    ref={(el) => { if (el) el.indeterminate = !allLoadedSelected && someLoadedSelected; }}
                    onChange={toggleSelectLoaded}
                    style={{ accentColor: '#6366f1', cursor: 'pointer' }}
                  />
                </th>
              )}
              {visibleCols.map((c) => {
                const filterActive = (headerFilter.columnFilters[c.key]?.length > 0) || !!headerFilter.filterMode[c.key];
                return (
                  <th
                    key={c.key}
                    style={{ ...thStyle, minWidth: colWidth(c), ...(autoFitWidths ? { width: colWidth(c), maxWidth: colWidth(c) } : {}) }}
                    onClick={() => toggleSort(c.key)}
                  >
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      <span>{c.label}{sortBy === c.key ? (sortDir === 'ASC' ? ' ▲' : ' ▼') : ''}</span>
                      {!NO_FILTER_COLUMNS.has(c.key) && (
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => headerFilter.openFilterAtPosition(e, c.key)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') headerFilter.openFilterAtPosition(e, c.key); }}
                          title={`Filter ${c.label}`}
                          style={{ cursor: 'pointer', display: 'inline-flex', opacity: filterActive ? 1 : 0.55 }}
                        >
                          <FilterIcon isActive={filterActive} color="#9ca3af" />
                        </span>
                      )}
                    </span>
                  </th>
                );
              })}
              {canEdit && <th style={{ ...thStyle, cursor: 'default', minWidth: ACTIONS_WIDTH, ...(autoFitWidths ? { width: ACTIONS_WIDTH, maxWidth: ACTIONS_WIDTH } : {}) }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={visibleCols.length + (canEdit ? 2 : 0)} style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>Loading...</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={visibleCols.length + (canEdit ? 2 : 0)} style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>No parts found.</td></tr>
            ) : items.map((item) => {
              const isEditing = editingId === item.id;
              if (isEditing) {
                return (
                  <tr key={item.id} style={{ backgroundColor: '#fefce8' }}>
                    {canEdit && <td style={tdStyle} />}
                    {visibleCols.map((c) => (
                      <td key={c.key} style={tdStyle}>
                        {c.editable && c.options ? (
                          <select
                            style={inputStyle}
                            value={editForm[c.key] ?? ''}
                            onChange={(e) => setEditForm({ ...editForm, [c.key]: e.target.value })}
                          >
                            <option value="">--</option>
                            {c.options.map((opt) => (<option key={opt} value={opt}>{opt}</option>))}
                          </select>
                        ) : c.editable ? (
                          <input
                            style={inputStyle}
                            type={c.numeric ? 'number' : 'text'}
                            step={c.numeric ? '0.01' : undefined}
                            value={editForm[c.key] ?? ''}
                            onChange={(e) => setEditForm({ ...editForm, [c.key]: e.target.value })}
                          />
                        ) : (
                          <span style={{ fontWeight: '600', color: '#1f2937' }}>{item[c.key]}</span>
                        )}
                      </td>
                    ))}
                    <td style={{ ...tdStyle, overflow: 'visible', whiteSpace: 'normal', minWidth: ACTIONS_WIDTH, ...(autoFitWidths ? { width: ACTIONS_WIDTH } : {}) }}>
                      <div style={{ display: 'flex', gap: '3px' }}>
                        <button onClick={handleSave} style={{ padding: '3px 8px', backgroundColor: '#198754', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.7rem' }}>Save</button>
                        <button onClick={() => setEditingId(null)} style={{ padding: '3px 8px', backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '4px', cursor: 'pointer', fontSize: '0.7rem' }}>Cancel</button>
                      </div>
                    </td>
                  </tr>
                );
              }
              const isSelected = selectAllMatching || selectedIds.has(item.id);
              return (
                <tr key={item.id}
                  style={isSelected ? { backgroundColor: '#eef2ff' } : undefined}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = isSelected ? '#e0e7ff' : '#fafbfc')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = isSelected ? '#eef2ff' : 'transparent')}>
                  {canEdit && (
                    <td style={{ ...tdStyle, width: 34, minWidth: 34 }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelect(item.id)}
                        style={{ accentColor: '#6366f1', cursor: 'pointer' }}
                      />
                    </td>
                  )}
                  {visibleCols.map((c) => (
                    c.key === 'part_number' ? (
                      <td key={c.key} style={{ ...tdStyle, fontWeight: '600', fontFamily: 'monospace' }}>
                        {item.flagged && (
                          <span title={`Flagged${item.flagged_by ? ` by ${item.flagged_by}` : ''}${item.flag_reason ? `: ${item.flag_reason}` : ''}`} style={{ marginRight: 4, cursor: 'help' }}>🚩</span>
                        )}
                        <button onClick={() => openPriceHistory(item)} title="View price history"
                          style={{ background: 'none', border: 'none', padding: 0, color: '#0d6efd', cursor: 'pointer', fontWeight: '600', fontFamily: 'monospace', fontSize: '0.78rem', textDecoration: 'underline' }}>
                          {item.part_number}
                        </button>
                      </td>
                    ) : c.key === 'datasheet' ? (
                      <td key={c.key} style={{ ...tdStyle, overflow: 'visible', whiteSpace: 'normal' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {item.current_datasheet_id ? (
                            <button onClick={() => viewDatasheet(item.current_datasheet_id)} title={item.datasheet_filename || 'View datasheet'}
                              style={{ background: 'none', border: 'none', padding: 0, color: '#0d6efd', cursor: 'pointer', fontSize: '0.75rem', textDecoration: 'underline', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 110 }}>
                              📄 {item.datasheet_filename || 'View'}
                            </button>
                          ) : (
                            <span style={{ color: '#cbd5e1', fontSize: '0.75rem' }}>None</span>
                          )}
                          <button onClick={() => openDatasheetHistory(item)}
                            title={`Datasheet versions${Number(item.datasheet_versions) > 0 ? ` (${item.datasheet_versions} on file)` : ''}`}
                            style={{ background: Number(item.datasheet_versions) > 1 ? '#eef2ff' : 'none', border: '1px solid #e5e7eb', borderRadius: 4, padding: '1px 5px', cursor: 'pointer', fontSize: '0.7rem', color: Number(item.datasheet_versions) > 1 ? '#4f46e5' : '#6b7280', fontWeight: Number(item.datasheet_versions) > 1 ? 600 : 400 }}>
                            ⧉{Number(item.datasheet_versions) > 1 ? ` ${item.datasheet_versions}` : ''}
                          </button>
                        </div>
                      </td>
                    ) : (
                      <td key={c.key} style={{ ...tdStyle, ...(c.numeric ? { textAlign: 'right' } : {}) }}>
                        {fmtVal(c, item[c.key])}
                      </td>
                    )
                  ))}
                  {canEdit && (
                    <td style={{ ...tdStyle, overflow: 'visible', whiteSpace: 'normal', minWidth: ACTIONS_WIDTH, ...(autoFitWidths ? { width: ACTIONS_WIDTH } : {}) }}>
                      <div style={{ display: 'flex', gap: '3px', flexWrap: 'wrap' }}>
                        <button onClick={() => startEdit(item)} style={{ padding: '2px 6px', backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '4px', cursor: 'pointer', fontSize: '0.7rem' }}>Edit</button>
                        {item.flagged ? (
                          <button onClick={() => handleUnflag(item)} title="Clear flag" style={{ padding: '2px 6px', backgroundColor: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '4px', cursor: 'pointer', fontSize: '0.7rem', color: '#c2410c' }}>Unflag</button>
                        ) : (
                          <button onClick={() => handleFlag(item)} title="Flag this rate" style={{ padding: '2px 6px', backgroundColor: '#fff7ed', border: '1px solid #fed7aa', borderRadius: '4px', cursor: 'pointer', fontSize: '0.7rem', color: '#c2410c' }}>🚩 Flag</button>
                        )}
                        <button onClick={() => handleRevertCost(item)} title="Revert cost to previous price" style={{ padding: '2px 6px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '4px', cursor: 'pointer', fontSize: '0.7rem', color: '#1d4ed8' }}>↩ Price</button>
                        <button onClick={() => handleDelete(item.id)} style={{ padding: '2px 6px', backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '4px', cursor: 'pointer', fontSize: '0.7rem', color: '#dc3545' }}>Del</button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Row counter (infinite scroll replaces pagination) */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px', flexWrap: 'wrap', gap: '10px' }}>
        <span style={{ color: '#6b7280', fontSize: '0.82rem' }}>
          Showing {items.length.toLocaleString()} of {total.toLocaleString()} rows
          {hasMore && !loadingMore && ' — scroll down to load more'}
          {loadingMore && ' — loading…'}
        </span>
      </div>

      <GridExportModal
        isOpen={showExport}
        onClose={() => setShowExport(false)}
        onExport={handleExport}
        isExporting={exporting}
        title="Export Part Rate DB"
        accentColor="#198754"
        visibleCount={visibleColumns.size}
        totalCount={COLUMNS.length}
      />
      <UploadModal
        isOpen={showImport}
        onClose={() => setShowImport(false)}
        onUploadSuccess={(data) => { showToast(`Imported ${data.rowsProcessed || 0} parts`); fetchItems(); }}
        onUploadError={(msg) => showToast(msg || 'Import failed', 'error')}
        config={UPLOAD_CONFIGS.PART_RATE_DB}
      />
      <AuditLogModal isOpen={showAudit} onClose={() => setShowAudit(false)} endpoint="/lt-part-rate-db/audit-log" title="Part Rate DB Audit Log" accentColor="#198754" />

      <PartRateDbAnalytics
        open={showAnalytics}
        onClose={() => setShowAnalytics(false)}
        instance={instance}
        accounts={accounts}
        filters={filters}
        search={search}
      />

      {showHelp && (
        <div onClick={() => setShowHelp(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '10px', maxWidth: '720px', width: '100%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}>
            <div style={{ position: 'sticky', top: 0, background: 'linear-gradient(135deg, #198754 0%, #146c43 100%)', color: 'white', padding: '0.9rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1.05rem' }}>🧱 Part Rate DB — How it works</h3>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <ListenButton compact guideId="part-rate-db-help" title="Part Rate DB Help" accent="#198754" dark />
                <button onClick={() => setShowHelp(false)} style={{ background: 'transparent', border: 'none', color: 'white', fontSize: '1.4rem', cursor: 'pointer', lineHeight: 1 }}>×</button>
              </div>
            </div>
            <div style={{ padding: '1.25rem 1.5rem', fontSize: '0.85rem', color: '#374151', lineHeight: 1.6 }}>
              <HelpWithVideoTour guideId="part-rate-db-help" title="Part Rate DB Help" accent="#198754">
              <h4 style={{ margin: '0 0 .35rem' }}>What this is</h4>
              <p style={{ margin: '0 0 .9rem' }}>The reference catalogue of parts with labour hours and purchase costs. Every MTO upload matches against this DB to bring in <strong>Part #</strong>, <strong>OTTAV hours</strong> (MH per EA), <strong>Joints</strong>, <strong>Joint Type</strong>, <strong>Dia In</strong>, and <strong>Unit Price (Cost CAD)</strong>. Matching uses 5 columns: type, material, size, SCH, rating.</p>

              <h4 style={{ margin: '0 0 .35rem' }}>Hours: OTTAV vs MCAA</h4>
              <p style={{ margin: '0 0 .9rem' }}><strong>OTTAV hours</strong> (the <em>OTT Hour</em> column) are the single basis for all MTO hour and labour calculations. <strong>MCAA</strong> hours/rates are kept for reference but are <strong>set aside for now</strong> and do not drive MTO totals.</p>

              <h4 style={{ margin: '0 0 .35rem' }}>Purchasing ↔ Part Rate ↔ MTO</h4>
              <ol style={{ margin: '0 0 .9rem', paddingLeft: '1.2rem' }}>
                <li>From the MTO, the TC uses <strong>Send to Purchasing</strong> — one Excel per What-to-send category (Valves, CS, …).</li>
                <li>Purchasing returns the file(s) with <strong>PRICE</strong> filled in.</li>
                <li>Here, use <strong>💲 Import Cost</strong>: costs overwrite the current <strong>Cost CAD</strong>, are kept in history, and are <strong>pushed onto matching MTO rows</strong> automatically.</li>
                <li>If the same part number has <strong>more than one different cost</strong> in the import file, those parts are skipped and you get a list (with CSV download) so you can fix and re-import.</li>
              </ol>
              <p style={{ margin: '0 0 .9rem' }}><strong>Click a Part #</strong> to open its price trend over time.</p>

              <h4 style={{ margin: '0 0 .35rem' }}>Joints &amp; Dia In</h4>
              <p style={{ margin: '0 0 .9rem' }}><strong>Joints</strong> and <strong>Dia In</strong> (per-each) are pulled onto the MTO on match. On the MTO: <strong>Total Joints</strong> = Joints × Qty; <strong>Total Dia In</strong> = Dia In × Qty. Joint Type is also carried for analytics (Dia In by Joint Type, Joint Type pivot).</p>

              <h4 style={{ margin: '0 0 .35rem' }}>Growing the DB</h4>
              <p style={{ margin: 0 }}>When an MTO upload contains parts that aren't in this DB, those rows are <strong>rejected</strong> and the uploader is emailed the missing list — add them here first, then re-upload. Piping estimators are notified of new parts so a lead can review rates. Slow and deliberate keeps both the catalogue and the MTO trustworthy.</p>
              </HelpWithVideoTour>
            </div>
          </div>
        </div>
      )}
      <UploadModal
        isOpen={showCostImport}
        onClose={() => setShowCostImport(false)}
        onUploadSuccess={(data) => {
          // Show the server's honest breakdown. If nothing updated or parts were
          // unmatched / multi-cost conflicts, flag it so it isn't mistaken for a full import.
          const conflicts = Array.isArray(data.conflicts) ? data.conflicts : [];
          if (conflicts.length) setCostConflicts(conflicts);
          const hadProblems = (data.updated ?? data.rowsProcessed ?? 0) === 0
            || (data.notFound || 0) > 0
            || (data.badValue || 0) > 0
            || conflicts.length > 0;
          showToast(data.message || `Updated ${data.rowsProcessed || 0} prices`, hadProblems ? 'error' : 'success');
          fetchItems();
        }}
        onUploadError={(msg) => showToast(msg || 'Cost import failed', 'error')}
        config={UPLOAD_CONFIGS.PART_RATE_COST}
      />

      {costConflicts && (
        <div onClick={() => setCostConflicts(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: 10, maxWidth: 720, width: '100%', maxHeight: '85vh', overflow: 'hidden', boxShadow: '0 10px 40px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ background: '#b45309', color: 'white', padding: '0.9rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1rem' }}>⚠ Multiple costs for same part ({costConflicts.length})</h3>
              <button type="button" onClick={() => setCostConflicts(null)} style={{ background: 'transparent', border: 'none', color: 'white', fontSize: '1.4rem', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: '1rem 1.25rem', overflowY: 'auto' }}>
              <p style={{ margin: '0 0 0.75rem', fontSize: '0.85rem', color: '#374151' }}>
                These part numbers appeared more than once in the import file with <strong>different</strong> costs.
                None of their prices were applied — fix the file (one cost per part) and re-import, or leave them unchanged.
              </p>
              <div style={{ border: '1px solid #e2e8f0', borderRadius: 6, maxHeight: 360, overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                  <thead>
                    <tr style={{ background: '#fffbeb', position: 'sticky', top: 0 }}>
                      <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #fde68a' }}>Part #</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #fde68a' }}>Costs found</th>
                      <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #fde68a' }}>Excel rows</th>
                    </tr>
                  </thead>
                  <tbody>
                    {costConflicts.map((c) => (
                      <tr key={c.part_number}>
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9', fontWeight: 600 }}>{c.part_number}</td>
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9' }}>
                          {(c.costs || []).map((n) => `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}`).join(' · ')}
                        </td>
                        <td style={{ padding: '6px 8px', borderBottom: '1px solid #f1f5f9', color: '#64748b', fontSize: '0.75rem' }}>
                          {(c.rows_by_cost || []).map((r) => `${r.cost}: rows ${r.rows.join(', ')}`).join(' | ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 12 }}>
                <button
                  type="button"
                  onClick={() => {
                    const header = 'part_number,costs,excel_rows\n';
                    const body = costConflicts.map((c) => {
                      const costs = (c.costs || []).join(' | ');
                      const rows = (c.rows_by_cost || []).map((r) => `${r.cost}:[${r.rows.join(';')}]`).join(' / ');
                      return `"${c.part_number}","${costs}","${rows}"`;
                    }).join('\n');
                    const blob = new Blob([header + body], { type: 'text/csv;charset=utf-8' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `part-rate-cost-conflicts-${new Date().toISOString().slice(0, 10)}.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                  }}
                  style={{ ...BTN, backgroundColor: '#fffbeb', borderColor: '#fbbf24', color: '#92400e' }}
                >
                  Download CSV
                </button>
                <button type="button" onClick={() => setCostConflicts(null)} style={{ ...BTN, backgroundColor: '#0d6efd', color: 'white', borderColor: '#0d6efd' }}>Close</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {historyPart && (
        <div onClick={() => setHistoryPart(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '10px', maxWidth: '720px', width: '100%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}>
            <div style={{ position: 'sticky', top: 0, background: '#0d6efd', color: 'white', padding: '0.9rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1rem' }}>💲 Price history — Part {historyPart}</h3>
              <button onClick={() => setHistoryPart(null)} style={{ background: 'transparent', border: 'none', color: 'white', fontSize: '1.4rem', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: '1.25rem' }}>
              {historyLoading ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Loading…</div>
              ) : historyData.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>No price changes recorded yet for this part.</div>
              ) : (
                <>
                  <div style={{ height: 240, marginBottom: '1rem' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={historyData.map((h) => ({ date: new Date(h.changed_at).toLocaleDateString(), cost: h.cost_cad === null ? null : Number(h.cost_cad) }))} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                        <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} width={60} tickFormatter={(v) => `$${v}`} />
                        <Tooltip formatter={(v) => [`$${Number(v).toFixed(2)}`, 'Cost CAD']} />
                        <Line type="monotone" dataKey="cost" stroke="#0d6efd" strokeWidth={2} dot={{ r: 3 }} connectNulls />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                    <thead>
                      <tr style={{ background: '#f9fafb', color: '#6b7280', textAlign: 'left' }}>
                        <th style={{ padding: '6px 8px' }}>Date</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right' }}>Cost CAD</th>
                        <th style={{ padding: '6px 8px', textAlign: 'right' }}>Previous</th>
                        <th style={{ padding: '6px 8px' }}>Source</th>
                        <th style={{ padding: '6px 8px' }}>By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...historyData].reverse().map((h) => (
                        <tr key={h.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                          <td style={{ padding: '5px 8px' }}>{new Date(h.changed_at).toLocaleString()}</td>
                          <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 600 }}>{h.cost_cad === null ? '—' : `$${Number(h.cost_cad).toFixed(2)}`}</td>
                          <td style={{ padding: '5px 8px', textAlign: 'right', color: '#9ca3af' }}>{h.previous_cost_cad === null || h.previous_cost_cad === undefined ? '—' : `$${Number(h.previous_cost_cad).toFixed(2)}`}</td>
                          <td style={{ padding: '5px 8px' }}>{h.source}</td>
                          <td style={{ padding: '5px 8px' }}>{h.changed_by || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {dsItem && (
        <div onClick={() => setDsItem(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: 'white', borderRadius: '10px', maxWidth: '640px', width: '100%', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.25)' }}>
            <div style={{ position: 'sticky', top: 0, background: '#198754', color: 'white', padding: '0.9rem 1.25rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3 style={{ margin: 0, fontSize: '1rem' }}>📄 Datasheet versions — Part {dsItem.part_number}</h3>
              <button onClick={() => setDsItem(null)} style={{ background: 'transparent', border: 'none', color: 'white', fontSize: '1.4rem', cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ padding: '1.25rem' }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                <input ref={dsFileRef} type="file" accept="application/pdf" style={{ display: 'none' }} onChange={onDatasheetPick} />
                {canEdit && (
                  <button onClick={() => dsFileRef.current?.click()} disabled={dsUploading}
                    style={{ ...BTN, backgroundColor: '#198754', color: 'white', borderColor: '#198754', cursor: dsUploading ? 'not-allowed' : 'pointer' }}>
                    {dsUploading ? 'Uploading…' : '📎 Upload new version'}
                  </button>
                )}
              </div>
              {dsLoading ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Loading…</div>
              ) : dsData.length === 0 ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>No datasheet versions uploaded yet for this part.</div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                  <thead>
                    <tr style={{ background: '#f9fafb', color: '#6b7280', textAlign: 'left' }}>
                      <th style={{ padding: '6px 8px' }}>Uploaded</th>
                      <th style={{ padding: '6px 8px' }}>File</th>
                      <th style={{ padding: '6px 8px' }}>By</th>
                      <th style={{ padding: '6px 8px' }}>Source</th>
                      <th style={{ padding: '6px 8px', textAlign: 'center' }}>View</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dsData.map((d, i) => (
                      <tr key={d.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                        <td style={{ padding: '5px 8px' }}>
                          {new Date(d.created_at).toLocaleString()}
                          {i === 0 && <span style={{ marginLeft: 6, background: '#d1fae5', color: '#047857', borderRadius: 4, padding: '0 5px', fontSize: '0.65rem', fontWeight: 600 }}>CURRENT</span>}
                        </td>
                        <td style={{ padding: '5px 8px' }}>{d.original_filename || '—'}</td>
                        <td style={{ padding: '5px 8px' }}>{d.uploaded_by || '—'}</td>
                        <td style={{ padding: '5px 8px' }}>{d.source || '—'}</td>
                        <td style={{ padding: '5px 8px', textAlign: 'center' }}>
                          <button onClick={() => viewDatasheet(d.id)} style={{ background: 'none', border: 'none', color: '#0d6efd', cursor: 'pointer', textDecoration: 'underline', fontSize: '0.75rem' }}>Open</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {headerFilter.openFilterColumn && (
        <FilterTemplateDropdown
          columnKey={headerFilter.openFilterColumn}
          columnLabel={headerFilter.columnLabels[headerFilter.openFilterColumn]}
          showTextFilters={true}
          showValuesTab={DISTINCT_COLUMNS.includes(headerFilter.openFilterColumn)}
          isNumericColumn={headerFilter.numericColumnKeys.includes(headerFilter.openFilterColumn)}
          columnFilters={headerFilter.columnFilters}
          filterMode={headerFilter.filterMode}
          filterText={headerFilter.filterText}
          filterSearch={headerFilter.filterSearch}
          filterButtonPositions={headerFilter.filterButtonPositions}
          setOpenFilterColumn={headerFilter.setOpenFilterColumn}
          setFilterSearch={headerFilter.setFilterSearch}
          getUniqueValues={headerFilter.getUniqueValues}
          handleFilterToggle={headerFilter.handleFilterToggle}
          selectAllColumnFilter={headerFilter.selectAllColumnFilter}
          clearColumnFilter={headerFilter.clearColumnFilter}
          applyTextFilter={headerFilter.applyTextFilter}
        />
      )}
    </div>
  );
};

export default PartRateDbView;
